import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { crmService } from '../../services/crmService';
import { TrackRecord } from '../../types/crm';

const Container = styled.div`
    padding: 2rem;
    max-width: 100%;
    overflow-x: auto;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
`;

const Title = styled.h1`
    font-size: 1.5rem;
    color: #111827;
`;

const Controls = styled.div`
    display: flex;
    gap: 1rem;
`;

const SearchInput = styled.input`
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    width: 300px;
`;

const FilterSelect = styled.select`
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 4px;
`;

const TableContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    margin-top: 1rem;
`;

const Table = styled.table`
    min-width: 100%;
    width: max-content;
    border-collapse: collapse;
    background: white;
`;

const Th = styled.th`
    text-align: left;
    padding: 1rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    font-weight: 600;
    color: #374151;
    font-size: 0.875rem;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 10;
`;

const Td = styled.td`
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;
    color: #1f2937;
    font-size: 0.875rem;
    white-space: nowrap;

    input, select, textarea {
        width: 100%;
        min-width: 150px; /* Prevent collapse */
        padding: 0.25rem;
        border: 1px solid transparent;
        border-radius: 4px;
        background: transparent;
        transition: all 0.2s;
        font-family: inherit;
        font-size: inherit;

        &:hover {
            border-color: #d1d5db;
            background: #f9fafb;
        }

        &:focus {
            border-color: #2563eb;
            background: white;
            outline: none;
        }
    }

    textarea {
        white-space: pre-wrap;
        resize: vertical;
        min-height: 60px;
    }
`;

const Checkbox = styled.input`
    width: auto !important;
`;

const Pagination = styled.div`
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;
`;

const Button = styled.button`
    padding: 0.5rem 1rem;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    &:hover:not(:disabled) {
        background: #f3f4f6;
    }
`;

const PublisherLink = styled.span`
    color: #2563eb;
    cursor: pointer;
    text-decoration: none;
    &:hover {
        text-decoration: underline;
        color: #1d4ed8;
    }
`;

const EmailIcon = styled.span`
    margin-left: 8px;
    color: #2563eb;
    cursor: pointer;
    font-size: 1rem;
    &:hover {
        color: #1d4ed8;
    }
`;

const TracksCRM: React.FC = () => {
    const navigate = useNavigate();
    const [tracks, setTracks] = useState<TrackRecord[]>([]);
    const [allTracks, setAllTracks] = useState<TrackRecord[]>([]); // All tracks for grouping
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(50);

    const [search, setSearch] = useState('');
    const [mediaTypeFilter, setMediaTypeFilter] = useState('');
    const [rightsFilter, setRightsFilter] = useState('');

    const [groupBy, setGroupBy] = useState<'none' | 'publisher' | 'artist'>('none');

    // Debounce search
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Fetch all tracks when grouping is enabled
    const fetchAllTracks = useCallback(async () => {
        if (groupBy === 'none') {
            setAllTracks([]);
            return;
        }

        setLoading(true);
        try {
            // Fetch all tracks with current filters (no pagination)
            const response = await crmService.listTracks({
                q: debouncedSearch,
                media_type: mediaTypeFilter,
                rights_owned: rightsFilter,
                page: 1,
                limit: 10000 // Large limit to get all tracks
            });
            setAllTracks((response as any).tracks || []);
        } catch (error) {
            console.error("Error fetching all tracks:", error);
            setAllTracks([]);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, mediaTypeFilter, rightsFilter, groupBy]);

    // Fetch paginated tracks when not grouping
    const fetchTracks = useCallback(async () => {
        if (groupBy !== 'none') {
            // When grouping, we'll use allTracks instead
            return;
        }

        setLoading(true);
        try {
            const response = await crmService.listTracks({
                q: debouncedSearch,
                media_type: mediaTypeFilter,
                rights_owned: rightsFilter,
                page,
                limit
            });
            setTracks((response as any).tracks);
            setTotal(response.total);
        } catch (error) {
            console.error("Error fetching tracks:", error);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, mediaTypeFilter, rightsFilter, page, limit, groupBy]);

    // Fetch all tracks when grouping is enabled
    useEffect(() => {
        if (groupBy !== 'none') {
            fetchAllTracks();
        }
    }, [fetchAllTracks, groupBy]);

    // Fetch paginated tracks when not grouping
    useEffect(() => {
        if (groupBy === 'none') {
            fetchTracks();
        }
    }, [fetchTracks, groupBy]);

    const handleLocalChange = (trackId: string, field: string, value: any) => {
        const updateTrack = (track: TrackRecord): TrackRecord => {
            if (track.id !== trackId) return track;

            // Handle nested updates for local state
            const updatedTrack = { ...track };

            if (field === 'publisher_name') {
                updatedTrack.publisher = {
                    ...updatedTrack.publisher,
                    name: value,
                    id: updatedTrack.publisher?.id || 0,
                    contacts: updatedTrack.publisher?.contacts || []
                } as any;
            } else if (field.startsWith('contact_')) {
                const contactField = field.replace('contact_', '');
                const contacts = [...(updatedTrack.publisher?.contacts || [])];

                if (contacts.length === 0) {
                    contacts.push({ id: 0, name: '', email: '', phone: '', role: 'Primary', publisher_id: updatedTrack.publisher?.id || 0 });
                }

                contacts[0] = { ...contacts[0], [contactField]: value };

                updatedTrack.publisher = {
                    ...updatedTrack.publisher,
                    contacts
                } as any;
            } else {
                (updatedTrack as any)[field] = value;
            }

            return updatedTrack;
        };

        // Update tracks (for non-grouped view)
        setTracks(prev => prev.map(updateTrack));
        
        // Update allTracks (for grouped view)
        if (groupBy !== 'none' && allTracks.length > 0) {
            setAllTracks(prev => prev.map(updateTrack));
        }
    };

    const handleSave = async (trackId: string, field: string, value: any) => {
        console.log(`[SAVE] Attempting to save ${field} for track ${trackId}:`, value);
        try {
            await crmService.updateTrack(trackId, { [field]: value });
            console.log(`[SAVE] ✓ Successfully saved ${field}`);
        } catch (error: any) {
            console.error('[SAVE] ✗ Error saving track:', error);
            console.error('[SAVE] Error details:', error.response?.data);
            // Silently fail - user will see in console if needed
        }
    };

    const groupedTracks = useMemo(() => {
        if (groupBy === 'none') {
            return { 'All': tracks };
        }

        // Use allTracks for grouping (all tracks across all pages)
        const tracksToGroup = allTracks.length > 0 ? allTracks : tracks;
        const groups: Record<string, TrackRecord[]> = {};
        
        tracksToGroup.forEach(track => {
            let key = 'Unknown';
            if (groupBy === 'publisher') {
                key = track.publisher?.name || 'Unknown Publisher';
            } else if (groupBy === 'artist') {
                key = track.artist || 'Unknown Artist';
            }

            if (!groups[key]) groups[key] = [];
            groups[key].push(track);
        });

        // Sort keys
        const sortedGroups = Object.keys(groups).sort().reduce((acc, key) => {
            acc[key] = groups[key];
            return acc;
        }, {} as Record<string, TrackRecord[]>);

        // Update total for pagination (total number of tracks across all groups)
        const totalTracks = Object.values(sortedGroups).flat().length;
        setTotal(totalTracks);

        return sortedGroups;
    }, [tracks, allTracks, groupBy]);

    // Paginate grouped tracks for display
    const paginatedGroupedTracks = useMemo(() => {
        if (groupBy === 'none') {
            return groupedTracks;
        }

        // Flatten all groups into a single array while preserving group structure
        const allGroupedTracks: Array<{ groupName: string; track: TrackRecord }> = [];
        Object.entries(groupedTracks).forEach(([groupName, groupTracks]) => {
            groupTracks.forEach(track => {
                allGroupedTracks.push({ groupName, track });
            });
        });

        // Apply pagination
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedItems = allGroupedTracks.slice(startIndex, endIndex);

        // Rebuild grouped structure from paginated items
        const paginatedGroups: Record<string, TrackRecord[]> = {};
        paginatedItems.forEach(({ groupName, track }) => {
            if (!paginatedGroups[groupName]) {
                paginatedGroups[groupName] = [];
            }
            paginatedGroups[groupName].push(track);
        });

        return paginatedGroups;
    }, [groupedTracks, page, limit, groupBy]);

    return (
        <Container>
            <Header>
                <Title>Tracks CRM ({total})</Title>
                <Controls>
                    <SearchInput
                        placeholder="Search tracks..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <FilterSelect value={mediaTypeFilter} onChange={e => setMediaTypeFilter(e.target.value)}>
                        <option value="">All Media Types</option>
                        <option value="45">45</option>
                        <option value="acetate">Acetate</option>
                        <option value="tape">Tape</option>
                        <option value="none">None</option>
                    </FilterSelect>
                    <FilterSelect value={rightsFilter} onChange={e => setRightsFilter(e.target.value)}>
                        <option value="">All Rights</option>
                        <option value="master">Master</option>
                        <option value="publishing">Publishing</option>
                        <option value="both">Both</option>
                        <option value="neither">Neither</option>
                    </FilterSelect>
                    <FilterSelect value={groupBy} onChange={e => {
                        setGroupBy(e.target.value as any);
                        setPage(1); // Reset to first page when grouping changes
                    }}>
                        <option value="none">No Grouping</option>
                        <option value="publisher">Group by Publisher</option>
                        <option value="artist">Group by Artist</option>
                    </FilterSelect>
                </Controls>
            </Header>

            {loading ? (
                <div>Loading...</div>
            ) : (
                <>
                    <TableContainer>
                        <Table>
                            <thead>
                                <tr>
                                    <Th>Title</Th>
                                    <Th>Artist</Th>
                                    <Th>Media Type</Th>
                                    <Th>Rights</Th>
                                    <Th>Web?</Th>
                                    <Th>Publisher</Th>
                                    <Th>Contact</Th>
                                    <Th>Email</Th>
                                    <Th>Phone</Th>
                                    <Th>ISWC</Th>
                                    <Th>ISRC</Th>
                                    <Th>Notes</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(groupBy === 'none' ? groupedTracks : paginatedGroupedTracks).map(([groupName, groupTracks]) => {
                                    // Get publisher ID from first track if grouping by publisher
                                    const publisherId = groupBy === 'publisher' && groupTracks.length > 0 
                                        ? groupTracks[0].publisher_id 
                                        : null;
                                    
                                    const handlePublisherClick = () => {
                                        if (publisherId) {
                                            navigate(`/admin/crm/publisher/${publisherId}/communications`);
                                        }
                                    };
                                    
                                    return (
                                    <React.Fragment key={groupName}>
                                        {groupBy !== 'none' && (
                                            <tr style={{ background: '#f5f5f5' }}>
                                                <td colSpan={12} style={{ padding: '12px', fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                                                    {groupBy === 'publisher' && publisherId ? (
                                                        <>
                                                            <PublisherLink onClick={handlePublisherClick}>
                                                                {groupName}
                                                            </PublisherLink>
                                                            <EmailIcon onClick={handlePublisherClick} title="Send email to publisher">
                                                                📧
                                                            </EmailIcon>
                                                        </>
                                                    ) : (
                                                        groupName
                                                    )}
                                                    {' '}
                                                    <span style={{ fontWeight: 'normal', color: '#666', fontSize: '0.9em' }}>({groupTracks.length})</span>
                                                </td>
                                            </tr>
                                        )}
                                        {groupTracks.map(track => (
                                            <tr key={track.id}>
                                                <Td>{track.title}</Td>
                                                <Td>{track.artist}</Td>
                                                <Td>
                                                    <select
                                                        value={track.media_type || ''}
                                                        onChange={e => {
                                                            handleLocalChange(track.id, 'media_type', e.target.value);
                                                            handleSave(track.id, 'media_type', e.target.value);
                                                        }}
                                                    >
                                                        <option value="" disabled>Select...</option>
                                                        <option value="45">45</option>
                                                        <option value="acetate">Acetate</option>
                                                        <option value="tape">Tape</option>
                                                        <option value="none">None</option>
                                                    </select>
                                                </Td>
                                                <Td>
                                                    <select
                                                        value={track.rights_owned || ''}
                                                        onChange={e => {
                                                            handleLocalChange(track.id, 'rights_owned', e.target.value);
                                                            handleSave(track.id, 'rights_owned', e.target.value);
                                                        }}
                                                    >
                                                        <option value="" disabled>Select...</option>
                                                        <option value="master">Master</option>
                                                        <option value="publishing">Publishing</option>
                                                        <option value="both">Both</option>
                                                        <option value="neither">Neither</option>
                                                    </select>
                                                </Td>
                                                <Td style={{ textAlign: 'center' }}>
                                                    <Checkbox
                                                        type="checkbox"
                                                        checked={track.website_permission}
                                                        onChange={e => {
                                                            handleLocalChange(track.id, 'website_permission', e.target.checked);
                                                            handleSave(track.id, 'website_permission', e.target.checked);
                                                        }}
                                                    />
                                                </Td>
                                                <Td>
                                                    <input
                                                        value={track.publisher?.name || ''}
                                                        onChange={e => handleLocalChange(track.id, 'publisher_name', e.target.value)}
                                                        onBlur={e => handleSave(track.id, 'publisher_name', e.target.value)}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') {
                                                                e.currentTarget.blur();
                                                            }
                                                        }}
                                                        placeholder="Publisher..."
                                                    />
                                                </Td>
                                                <Td>
                                                    <input
                                                        value={track.publisher?.contacts?.[0]?.name || ''}
                                                        onChange={e => handleLocalChange(track.id, 'contact_name', e.target.value)}
                                                        onBlur={e => handleSave(track.id, 'contact_name', e.target.value)}
                                                        placeholder="Contact..."
                                                    />
                                                </Td>
                                                <Td>
                                                    <input
                                                        value={track.publisher?.contacts?.[0]?.email || ''}
                                                        onChange={e => handleLocalChange(track.id, 'contact_email', e.target.value)}
                                                        onBlur={e => handleSave(track.id, 'contact_email', e.target.value)}
                                                        placeholder="Email..."
                                                    />
                                                </Td>
                                                <Td>
                                                    <input
                                                        value={track.publisher?.contacts?.[0]?.phone || ''}
                                                        onChange={e => handleLocalChange(track.id, 'contact_phone', e.target.value)}
                                                        onBlur={e => handleSave(track.id, 'contact_phone', e.target.value)}
                                                        placeholder="Phone..."
                                                    />
                                                </Td>
                                                <Td>
                                                    <input
                                                        type="text"
                                                        value={track.iswc || ''}
                                                        onChange={e => handleLocalChange(track.id, 'iswc', e.target.value)}
                                                        onBlur={e => handleSave(track.id, 'iswc', e.target.value)}
                                                        maxLength={15}
                                                        placeholder="T-123456789-0"
                                                    />
                                                </Td>
                                                <Td>
                                                    <input
                                                        type="text"
                                                        value={track.isrc || ''}
                                                        onChange={e => handleLocalChange(track.id, 'isrc', e.target.value)}
                                                        onBlur={e => handleSave(track.id, 'isrc', e.target.value)}
                                                        maxLength={12}
                                                        placeholder="USRC17607839"
                                                    />
                                                </Td>
                                                <Td>
                                                    <textarea
                                                        value={track.notes || ''}
                                                        onChange={e => handleLocalChange(track.id, 'notes', e.target.value)}
                                                        onBlur={e => handleSave(track.id, 'notes', e.target.value)}
                                                        rows={2}
                                                        placeholder="Enter general notes..."
                                                    />
                                                </Td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </TableContainer>

                    <Pagination>
                        <Button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                        <span>Page {page} of {Math.ceil(total / limit)}</span>
                        <Button disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
                    </Pagination>
                </>
            )
            }
        </Container >
    );
};

export default TracksCRM;
